import { isDefined } from '@seaber/common/lib/generics';
import { last, sortBy } from 'lodash';
import { VesselSpeedType } from '../../graphql';
import type { ScenarioOrder } from '../../lib/planningData';
import { filteredMap, NONE } from '../../lib/util';
import type { Distances } from '../distances/context';
import {
  calculateSeaDuration,
  computeCapacityAtPorts,
  fallbackBallastSpeed,
  fallbackLadenSpeed,
  getDefaultVesselSpeedByType,
  getDistanceDeltaOnAddition,
  type LocationId,
} from './Core';
import type { SuggestionLogEntry } from './suggestions/getSuggestions';
import type { TimeData } from './TimeData';
import { getEventEndLocationId } from './TimeData';
import type { Vessel } from './Vessel';

export type ReachabilityResult =
  | { reachable: true }
  | { reachable: false; reason: 'laycan_unreachable'; earliestArrival: number }
  | { reachable: false; reason: 'excessive_ballast_distance' };

/** Check if there's any insertion position in the schedule from which the
 * order's load port is reachable before the cancelling AND within ballast distance limits. */
export function checkReachabilityFromAnyPosition(
  loadPort: LocationId,
  existingTds: readonly TimeData[],
  firstIndex: number,
  distances: Distances,
  avoidChannels: boolean,
  speedLaden: number,
  speedBallast: number,
  effectiveCancelling: number | undefined,
  maxBallastDistanceNm: number | null,
): ReachabilityResult {
  // No prior event to depart from — nothing to reject against
  if (existingTds.length === 0 || firstIndex <= 0) return { reachable: true };

  const lastEventBeforeInsertion = firstIndex - 1;

  // The first position has the earliest departure, giving the best possible arrival. If that misses, all miss
  if (effectiveCancelling !== undefined) {
    const td = existingTds[lastEventBeforeInsertion];
    const loc = getEventEndLocationId(td.event);
    const dist = distances.get(loc, loadPort, avoidChannels)?.dist ?? 0;
    const seaDur = calculateSeaDuration(
      speedLaden,
      speedBallast,
      dist,
      false,
      false,
    );
    const arrival = td.end + seaDur;
    if (arrival > effectiveCancelling) {
      return {
        reachable: false,
        reason: 'laycan_unreachable',
        earliestArrival: arrival,
      };
    }
  }

  if (maxBallastDistanceNm === null) return { reachable: true };

  // Ballast distance check
  for (let i = lastEventBeforeInsertion; i < existingTds.length; i++) {
    const loc = getEventEndLocationId(existingTds[i].event);
    const td = existingTds[i];
    const nextTd = existingTds.at(i + 1);
    // No placing in the middle of port calls
    if (nextTd && getEventEndLocationId(nextTd.event) === loc) continue;
    // Laden legs don't count as ballast violations
    if (td.ordersOnBoard > 0) return { reachable: true };

    const dist = distances.get(loc, loadPort, avoidChannels)?.dist ?? 0;
    if (dist <= maxBallastDistanceNm) return { reachable: true };
  }

  return { reachable: false, reason: 'excessive_ballast_distance' };
}
/** Get a short list of orders which might be good additions to the voyage.
 * This is very necessary when the order table contains hundreds or thousands of orders as we can't possibly iterate them all. */

export function shortlistOrdersForVoyageSuggestions(
  orders: Set<ScenarioOrder>,
  existingTds: TimeData[],
  firstIndex: number,
  assignedOrders: Set<ScenarioOrder>,
  isCombinationEvaluated: (orders: ScenarioOrder[]) => boolean,
  vessel: Vessel,
  distances: Distances,
  maxOrders: number,
  reporting: { logEntries: SuggestionLogEntry[] },
  {
    avoidChannels = false,
    maxIdleMs,
    loadLateToleranceMs = null,
    maxBallastDistanceNm = null,
  }: {
    avoidChannels?: boolean;
    maxIdleMs: number;
    /** Allowed late arrival tolerance for load events. When set, orders whose laycan end + tolerance is still before the earliest arrival are rejected. */
    loadLateToleranceMs?: number | null;
    /** Hard limit on ballast distance to load port. Orders whose minimum distance delta exceeds this are rejected. */
    maxBallastDistanceNm?: number | null;
  },
): ScenarioOrder[] {
  const speedLaden = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.LADEN,
    fallbackLadenSpeed,
  );
  const speedBallast = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.BALLAST,
    fallbackBallastSpeed,
  );

  const lastExistingTd = last(existingTds);
  const existingEnd = last(existingTds)?.end;
  const lastPort =
    lastExistingTd && getEventEndLocationId(lastExistingTd.event);
  const allowedOrders = [...orders.values()].filter((o) => {
    if (isCombinationEvaluated([...assignedOrders, o])) return null;

    const loadPort = o.loadPortId ?? (-1 as LocationId);
    const layday = o.loadingWindow.start;

    // Idle check: only from the last position.
    // The last position gives the shortest idle — earlier insertion points have
    // earlier arrivals, meaning even more idle until the layday.
    if (lastPort !== undefined && existingEnd !== undefined) {
      const distanceFromLast =
        distances.get(lastPort, loadPort, avoidChannels)?.dist ?? 0;
      const seaDurationFromLast = calculateSeaDuration(
        speedLaden,
        speedBallast,
        distanceFromLast,
        false,
        false,
      );
      const firstPossibleArrive = existingEnd + seaDurationFromLast;

      if (layday && layday - firstPossibleArrive > maxIdleMs) {
        reporting.logEntries.push({
          orderIds: [o.id],
          stage: 'shortlisting',
          reason: 'excessive_idle',
          arrivalTime: firstPossibleArrive,
        });
        return false;
      }
    }

    // Laycan reachability + ballast distance: check all possible insertion points.
    // This matters for the port pair dedup that follows: if one order of a port pair
    // has an unreachable laycan, early rejection here lets a valid sibling order
    // become the representative instead.
    const cancelling = o.loadingWindow.end;
    const effectiveCancelling =
      cancelling !== undefined && loadLateToleranceMs !== null
        ? cancelling + loadLateToleranceMs
        : cancelling;

    if (
      (effectiveCancelling !== undefined || maxBallastDistanceNm !== null) &&
      existingTds.length > 0
    ) {
      const result = checkReachabilityFromAnyPosition(
        loadPort,
        existingTds,
        firstIndex,
        distances,
        avoidChannels,
        speedLaden,
        speedBallast,
        effectiveCancelling,
        maxBallastDistanceNm,
      );
      if (!result.reachable) {
        reporting.logEntries.push(
          result.reason === 'excessive_ballast_distance'
            ? {
                orderIds: [o.id],
                stage: 'shortlisting' as const,
                reason: 'excessive_ballast_distance' as const,
              }
            : {
                orderIds: [o.id],
                stage: 'shortlisting' as const,
                reason: 'laycan_unreachable' as const,
                arrivalTime: result.earliestArrival,
              },
        );
        return false;
      }
    }

    // Note: discharge window is not checked here intentionally - it would make the check more expensive,
    // and it's usually safe to assume that if the load window is fine, the discharge window will be fine too.
    return true;
  });

  /** Each order by loadPort-dischargePort-assortmentId */
  const includedOrderKinds = new Set<string>();

  /** Only keep one order with the same load-discharge port pair and assortment to enable more variety in solution search. */
  const uniqueOrders = allowedOrders.filter((o) => {
    const { loadPortId, dischargePortId, assortmentId } = o;
    const key = `${loadPortId}-${dischargePortId}-${assortmentId}`;
    if (includedOrderKinds.has(key)) {
      reporting.logEntries.push({
        orderIds: [o.id],
        stage: 'shortlisting',
        reason: 'duplicate_port_pair',
      });
      return false;
    }
    includedOrderKinds.add(key);
    return true;
  });

  /** How much cargo can still be added on each port call */
  const capacityAtPorts = computeCapacityAtPorts(existingTds, firstIndex);
  if (existingTds.length > 0 && capacityAtPorts.length === 0) {
    reporting.logEntries.push({
      orderIds: uniqueOrders.map((o) => o.id),
      stage: 'shortlisting',
      reason: 'no_possible_ports',
    });
    return [];
  }

  /** Give each order a quick score for how good it might be if added to the voyage. */
  const scoredOrders: [ScenarioOrder, number][] = filteredMap(
    uniqueOrders,
    (o) => {
      const { loadPortId } = o;
      const fittingPorts = capacityAtPorts.filter(
        (c) =>
          c.capacity.volume >= o.cargoRanges.volume.min &&
          c.capacity.mass >= o.cargoRanges.mass.min,
      );
      if (capacityAtPorts.length > 0 && fittingPorts.length === 0) {
        reporting.logEntries.push({
          orderIds: [o.id],
          stage: 'shortlisting',
          reason: 'insufficient_capacity',
        });
        return NONE;
      }

      const distancesFromLoad = fittingPorts
        .map((c) =>
          getDistanceDeltaOnAddition(
            c.locationId,
            c.nextLocationId,
            loadPortId ?? (-1 as LocationId),
            distances,
            avoidChannels,
          ),
        )
        .filter(isDefined);
      const minDistanceFromLoad = Math.min(...distancesFromLoad);

      return [o, minDistanceFromLoad];
    },
  );

  const sorted = sortBy(scoredOrders, ([, score]) => score).map(([o]) => o);

  const selected = sorted.slice(0, maxOrders);
  const remaining = sorted.slice(maxOrders);
  reporting.logEntries.push({
    orderIds: remaining.map((o) => o.id),
    stage: 'shortlisting',
    reason: 'distance_from_ports',
  });
  reporting.logEntries.push({
    orderIds: selected.map((o) => o.id),
    stage: 'shortlisting',
    reason: 'selected',
  });
  return selected;
}
